
import type { Pack } from "../api";
import { Badge, Button, Card, cx } from "../components";
import { formatEuros, formatTokens, fromBigInt } from "../format";
import type { RangGamme } from "./AchatGamme";
import styles from "./AchatPage.module.css";

export type AchatPackCarteProps = {
  pack: Pack;
  /** Vrai quand ce pack est celui en cours de décision dans le panneau. */
  choisi: boolean;
  /** L'utilisateur désigne ce pack : le panneau de confirmation s'ouvre dessus. */
  onChoisir: (pack: Pack) => void;
  /** Vrai pendant l'envoi d'un achat : on ne change pas de pack en plein paiement. */
  envoiEnCours: boolean;
  /** Place du pack dans la gamme, calculée par `situerGamme`. */
  gamme: RangGamme;
};

/**
 * Une carte de pack du catalogue.
 *
 * Elle ne déclenche aucun paiement : elle **désigne** un pack, et c'est le
 * panneau de confirmation qui demande l'accord explicite. Deux gestes plutôt
 * qu'un, parce qu'un clic distrait sur une grille de cartes ne doit pas coûter
 * de l'argent.
 */
export function AchatPackCarte({
  pack,
  choisi,
  onChoisir,
  envoiEnCours,
  gamme,
}: AchatPackCarteProps) {
  const validite =
    pack.validity_days > 1
      ? `${pack.validity_days} jours`
      : `${pack.validity_days} jour`;

  return (
    <Card
      as="li"
      data-anim="card"
      className={cx(styles.cartePack, choisi && styles.choisie)}
      title={pack.label}
      actions={
        // La version de grille figurait ici sur les quatre cartes, identique
        // partout : du bruit. Elle reste là où elle prouve quelque chose, sur le
        // reçu et dans le grand livre. Le badge ne sert plus qu'à dire ce que le
        // bouton dit déjà, mais depuis le haut de la carte.
        choisi ? (
          <Badge tone="accent" size="sm">
            Retenu
          </Badge>
        ) : undefined
      }
      footer={
        <Button
          // Toujours `primary` : c'est l'action de l'écran. En `secondary`,
          // quatre boutons contournés sur quatre cartes blanches ne disaient
          // plus lequel était le geste attendu — et la page entière perdait son
          // point d'entrée. La sélection se lit sur la carte, pas sur le poids
          // du bouton.
          variant="primary"
          fullWidth
          disabled={envoiEnCours}
          // `aria-pressed` plutôt qu'un simple libellé : au lecteur d'écran, le
          // bouton annonce lui-même s'il est le pack retenu, sans obliger à
          // parcourir la page jusqu'au panneau pour le savoir.
          aria-pressed={choisi}
          onClick={() => onChoisir(pack)}
        >
          {choisi ? "Pack retenu" : "Choisir ce pack"}
        </Button>
      }
    >
      <p className={styles.prix}>
        <span className={cx(styles.prixMontant, "lde-tabular")}>
          {formatEuros(pack.price_cents)}
        </span>
      </p>

      {/* Le prix au token est ce qui distingue réellement une offre de la
          suivante. Sans lui, quatre cartes affichaient quatre prix sans jamais
          dire lequel était avantageux. */}
      <p className={styles.prixUnitaire}>
        <span className="lde-tabular">
          {formatEuros(fromBigInt(gamme.prixUnitaireCents))}
        </span>{" "}
        le token
      </p>

      {/* Les tokens sortent de la liste : c'est ce que le client achète, et le
          reste n'est qu'une modalité. */}
      <dl className={styles.tokens}>
        <dt className={styles.tokensLibelle}>Tokens crédités</dt>
        <dd className={cx(styles.tokensGagnes, "lde-tabular")}>
          {formatTokens(pack.tokens)}
        </dd>
      </dl>

      {/* Une liste de définitions plutôt qu'un tableau : une paire
          libellé / valeur se lit correctement au lecteur d'écran sans
          en-têtes de colonnes à inventer. */}
      <dl className={styles.caracteristiques}>
        <dt>Validité</dt>
        <dd>{validite}</dd>
      </dl>
    </Card>
  );
}
